/* eslint-disable i18next/no-literal-string */
/* NOTE: This file is exempted from strict i18n checks to maintain PR scope.
 * i18n improvements will be addressed in a dedicated PR (see Issue #1328).
 * This aligns with local ESLint config which already exempts this file.
 */

import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, type Variants } from 'framer-motion'
import { Lock, User, AlertCircle, Loader2 } from 'lucide-react'
import { AppleButton } from '@/components/ui/apple-button'
import { AppleInput } from '@/components/ui/apple-input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@morningai/shared-ui'
import { Alert, AlertDescription } from '@morningai/shared-ui'
import { Separator } from '@morningai/shared-ui'
import { LanguageSwitcher } from './LanguageSwitcher'
import { TwoFactorVerify } from './2fa/TwoFactorVerify'
import { TwoFactorEnroll } from './2fa/TwoFactorEnroll'
import apiClient from '@/lib/api'
import { bootstrapCsrf } from '@/lib/api-client'
import { signInWithOAuth } from '@/lib/supabaseClient'
import type { LoginResponse } from '@/types/2fa'

interface Credentials {
  email: string
  password: string
}

interface User {
  id: number
  name: string
  username: string
  role: string
  avatar: string | null
}

interface LoginPageProps {
  onLogin: (user: User) => void
}

const LoginPage = ({ onLogin }: LoginPageProps): React.ReactElement => {
  const { t } = useTranslation()
  const [credentials, setCredentials] = useState<Credentials>({
    email: '',
    password: ''
  })
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const [prefersReducedMotion, setPrefersReducedMotion] = useState<boolean>(false)
  const [show2FADialog, setShow2FADialog] = useState<boolean>(false)
  const [show2FAEnrollDialog, setShow2FAEnrollDialog] = useState<boolean>(false)
  const [tmpLoginToken, setTmpLoginToken] = useState<string>('')

  useEffect(() => {
    const mediaQuery: MediaQueryList = window.matchMedia('(prefers-reduced-motion: reduce)')
    setPrefersReducedMotion(mediaQuery.matches)
    
    const handleChange = (e: MediaQueryListEvent): void => setPrefersReducedMotion(e.matches)
    mediaQuery.addEventListener('change', handleChange)
    
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      await bootstrapCsrf()
      
      const result: LoginResponse = await apiClient.login(credentials)
      
      if (result.next_step) {
        if (result.tmp_login_token) {
          setTmpLoginToken(result.tmp_login_token)
        }
        
        if (result.next_step === 'enroll_2fa') {
          setShow2FAEnrollDialog(true)
          setLoading(false)
          return
        } else if (result.next_step === 'challenge_2fa') {
          setShow2FADialog(true)
          setLoading(false)
          return
        }
      }
      
      if (result.requires_2fa) {
        setShow2FADialog(true)
        setLoading(false)
        return
      }
      
      if (result.user) {
        await bootstrapCsrf()
        onLogin(result.user)
      } else {
        setError(result.message || t('auth.login.loginFailed'))
      }
    } catch (error) {
      if (import.meta.env.DEV && credentials.email === 'admin' && credentials.password === 'admin123') {
        const mockUser: User = {
          id: 1,
          name: t('sidebar.user.defaultName'),
          username: 'admin',
          role: t('sidebar.user.defaultRole'),
          avatar: null
        }
        onLogin(mockUser)
      } else {
        setError(t('auth.login.loginError'))
      }
    } finally {
      setLoading(false)
    }
  }

  const handle2FAVerify = async (params: {
    code: string;
    isBackup: boolean;
    rememberDevice: boolean;
  }) => {
    if (tmpLoginToken) {
      const { challengeTwoFA } = await import('@/lib/2fa-api')
      
      await challengeTwoFA({
        code: params.isBackup ? undefined : params.code,
        backup_code: params.isBackup ? params.code : undefined,
        remember_device: params.rememberDevice,
      }, tmpLoginToken)

      setShow2FADialog(false)
      setTmpLoginToken('')
      
      try {
        const user = await apiClient.getCurrentUser()
        onLogin(user)
      } catch (error) {
        setError(t('auth.login.loginError'))
      }
    } else {
      const { verifyTwoFALogin } = await import('@/lib/2fa-api')
      
      await verifyTwoFALogin({
        email: credentials.email,
        password: credentials.password,
        totp_code: params.isBackup ? undefined : params.code,
        backup_code: params.isBackup ? params.code : undefined,
        remember_device: params.rememberDevice,
      })

      setShow2FADialog(false)
      
      try {
        const user = await apiClient.getCurrentUser()
        onLogin(user)
      } catch (error) {
        setError(t('auth.login.loginError'))
      }
    }
  }

  const handle2FACancel = () => {
    setShow2FADialog(false)
    setShow2FAEnrollDialog(false)
    setTmpLoginToken('')
    setError('')
  }

  const handle2FAEnroll = async (params: {
    code: string;
    backupCodes: string[];
  }) => {
    setShow2FAEnrollDialog(false)
    setTmpLoginToken('')
    
    try {
      const user = await apiClient.getCurrentUser()
      onLogin(user)
    } catch (error) {
      setError(t('auth.login.loginError'))
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value
    })
  }

  const handleSSOLogin = async (provider: string): Promise<void> => {
    try {
      setLoading(true)
      setError('')
      
      const { error } = await signInWithOAuth(provider, {
        redirectTo: `${window.location.origin}/auth/callback`
      })
      
      if (error) {
        setError(t('auth.sso.initializationFailed'))
        setLoading(false)
      }
    } catch (error) {
      console.error('SSO login error:', error)
      setError(t('auth.sso.initializationFailed'))
      setLoading(false)
    }
  }

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  }

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-900">
      <motion.div
        style={{ position: 'fixed', top: '1rem', right: '1rem', zIndex: 50 }}
        initial={prefersReducedMotion ? {} : { opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <LanguageSwitcher variant="compact" />
      </motion.div>
      
      <motion.div
        className="w-full max-w-md px-4"
        variants={prefersReducedMotion ? undefined : containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div
          className="text-center mb-8"
          variants={prefersReducedMotion ? undefined : itemVariants}
        >
          <Link to="/" className="inline-block">
            <motion.div
              className="mx-auto w-16 h-16 mb-4"
              whileHover={prefersReducedMotion ? {} : { scale: 1.1, rotate: 5 }}
              transition={{ duration: 0.3 }}
            >
              <img 
                src="/assets/brand/icon-only/MorningAI_icon_1024.png" 
                alt="Morning AI" 
                className="w-full h-full rounded-2xl cursor-pointer"
                style={{ width: '64px', height: '64px', maxWidth: '64px', maxHeight: '64px' }}
              />
            </motion.div>
          </Link>
          <Link to="/" className="inline-block hover:opacity-80 transition-opacity">
            <h1 className="text-3xl font-bold text-neutral-900 dark:text-white">{t('app.name')}</h1>
          </Link>
          <p className="text-neutral-600 dark:text-neutral-300 mt-2">{t('app.tagline')}</p>
        </motion.div>

        <motion.div variants={prefersReducedMotion ? undefined : itemVariants}>
          <Card>
            <CardHeader>
              <CardTitle>{t('auth.login.title')}</CardTitle>
              <CardDescription>
                {t('auth.login.description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <motion.div
                    initial={prefersReducedMotion ? {} : { opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  </motion.div>
                )}

                <AppleInput
                  id="email"
                  name="email"
                  type="email"
                  label={t('auth.login.email')}
                  placeholder={t('auth.login.emailPlaceholder')}
                  value={credentials.email}
                  onChange={handleChange}
                  leftIcon={<User className="w-4 h-4" />}
                  required
                  haptic="light"
                />

                <AppleInput
                  id="password"
                  name="password"
                  type="password"
                  label={t('auth.login.password')}
                  placeholder={t('auth.login.passwordPlaceholder')}
                  value={credentials.password}
                  onChange={handleChange}
                  leftIcon={<Lock className="w-4 h-4" />}
                  showPasswordToggle
                  required
                  haptic="light"
                />

                <motion.div
                  whileHover={prefersReducedMotion ? {} : { scale: 1.02 }}
                  whileTap={prefersReducedMotion ? {} : { scale: 0.98 }}
                >
                  <AppleButton 
                    type="submit" 
                    className="w-full" 
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('auth.login.loggingIn')}
                      </>
                    ) : (
                      t('auth.login.loginButton')
                    )}
                  </AppleButton>
                </motion.div>
              </form>

              <div className="mt-6">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <Separator className="w-full" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white dark:bg-neutral-800 px-2 text-neutral-500 dark:text-neutral-300">
                      {t('auth.login.orContinueWith')}
                    </span>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-3 gap-3">
                  <AppleButton
                    type="button"
                    variant="outline"
                    onClick={() => handleSSOLogin('google')}
                    disabled={loading}
                    className="w-full"
                    aria-label={t('auth.sso.loginWithGoogle')}
                  >
                    <svg className="h-5 w-5" viewBox="0 0 24 24">
                      <path
                        fill="currentColor"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="currentColor"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                  </AppleButton>

                  <AppleButton
                    type="button"
                    variant="outline"
                    onClick={() => handleSSOLogin('apple')}
                    disabled={loading}
                    className="w-full"
                    aria-label={t('auth.sso.loginWithApple')}
                  >
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                    </svg>
                  </AppleButton>

                  <AppleButton
                    type="button"
                    variant="outline"
                    onClick={() => handleSSOLogin('github')}
                    disabled={loading}
                    className="w-full"
                    aria-label={t('auth.sso.loginWithGitHub')}
                  >
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.17 6.839 9.49.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.34-3.369-1.34-.454-1.156-1.11-1.463-1.11-1.463-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.831.092-.646.35-1.086.636-1.336-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0112 6.836c.85.004 1.705.114 2.504.336 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.167 22 16.418 22 12c0-5.523-4.477-10-10-10z" />
                    </svg>
                  </AppleButton>
                </div>
              </div>

              <div className="mt-6 text-center text-sm text-neutral-600 dark:text-neutral-300">
                {t('auth.login.noAccount', '還沒有帳號？')}{' '}
                {/* NOTE: Using primary-200 for CI color-contrast compliance. 
                    If reverting to primary-300 for brand consistency, adjust .lighthouserc precision settings. */}
                <Link to="/signup" className="text-primary-600 hover:text-primary-700 dark:text-primary-200 dark:hover:text-primary-100 font-medium">
                  {t('auth.login.signupLink', '註冊')}
                </Link>
              </div>

              {import.meta.env.DEV && (
                <div className="mt-6 p-4 bg-neutral-100 dark:bg-neutral-800 rounded-lg">
                  <h2 className="text-sm font-medium text-neutral-900 dark:text-white mb-2">{t('auth.login.devAccount')}</h2>
                  <div className="text-sm text-neutral-700 dark:text-neutral-300 space-y-1">
                    <p>{t('auth.login.username')}: <code className="bg-neutral-200 dark:bg-neutral-700 px-1 rounded">{t('auth.login.demoUsername', 'admin')}</code></p>
                    <p>{t('auth.login.password')}: <code className="bg-neutral-200 dark:bg-neutral-700 px-1 rounded">{t('auth.login.demoPassword', 'admin123')}</code></p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          className="text-center mt-8 text-sm text-neutral-600 dark:text-neutral-300"
          variants={prefersReducedMotion ? undefined : itemVariants}
        >
          <p>{t('app.copyright')}</p>
          <p className="mt-1">{t('app.motto')}</p>
        </motion.div>
      </motion.div>

      <TwoFactorVerify
        open={show2FADialog}
        onClose={handle2FACancel}
        onVerify={handle2FAVerify}
      />

      <TwoFactorEnroll
        open={show2FAEnrollDialog}
        onClose={handle2FACancel}
        onComplete={handle2FAEnroll}
        tmpLoginToken={tmpLoginToken}
      />
    </div>
  )
}

export default LoginPage

